/**
 * Guided-planning host for the shared coverage projection. Keeps guided's single forward choice and
 * its branching: the D2C consumer flow (until the guided cutover moves it), Genworth's funding plan,
 * skipping straight to plan comparison when policy steps are skipped, or the next wizard step.
 */
import { useCallback } from 'react';
import { useWizard } from 'react-use-wizard';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import { FundingAssessmentProjection } from '@/features/funding-sources';
import { StepLayout } from '@/features/guided-financial-planning/components/step-layout';
import { getStepIndex } from '@/features/guided-financial-planning/config';
import { useGuidedPlanningFlatStepConfigs } from '@/features/guided-financial-planning/context';
import { useSkipPolicySteps } from '@/features/guided-financial-planning/hooks';
import { useGuidedCoverageAssessment } from '@/features/guided-financial-planning/hooks/use-guided-coverage-assessment';
import { selectClient, selectFeatureFlags } from '@client/model/selectors';
import { useIsConsumerFlowEnabled } from '@/hooks/use-app-mode';
import { consumerStepPath, startGuidedJourney } from '@/features/consumer';

interface NextButtonLabelOptions {
  hasGap: boolean;
  isConsumerAppMode: boolean;
  isGenworthMode: boolean;
  isCcrcTypeA: boolean;
  skipPolicySteps: boolean;
}

function nextButtonLabelFor({
  hasGap,
  isConsumerAppMode,
  isGenworthMode,
  isCcrcTypeA,
  skipPolicySteps,
}: NextButtonLabelOptions): string {
  const protectionOptionsLabel = hasGap
    ? 'Yes, Find Insurance to Close the Gap'
    : 'Show Me Efficient Protection Options';

  if (isConsumerAppMode) return protectionOptionsLabel;
  if (isGenworthMode) return 'Back to My Funding Plan';
  if (isCcrcTypeA) return 'Show Me My Type A Plan';
  if (skipPolicySteps) return 'Continue to Plan Comparison';
  return protectionOptionsLabel;
}

export function AssessmentProjectionStep() {
  const { nextStep, previousStep, goToStep } = useWizard();
  const assessment = useGuidedCoverageAssessment();
  const hasGap = assessment.status === 'ready' && assessment.value.gap > 0;
  const featureFlags = useSelector(selectFeatureFlags);
  const client = useSelector(selectClient);
  const navigate = useNavigate();
  const isCcrcTypeA = !!featureFlags.ccrcTypeAProducts;
  const isGenworthMode = !!featureFlags.genworthMode;
  const skipPolicySteps = useSkipPolicySteps();
  const flatStepConfigs = useGuidedPlanningFlatStepConfigs();
  const isConsumerAppMode = useIsConsumerFlowEnabled();

  // Genworth doesn't sell or shop policies, so the coverage assessment is the last step —
  // the CTA returns to the funding plan instead of advancing into policy selection.
  // In the d2c app mode, policy options continue in the guided consumer flow.
  const handleNext = useCallback(() => {
    if (isConsumerAppMode && client) {
      startGuidedJourney(client.clientId);
      navigate(consumerStepPath(client.clientId, 'payment-preference'));
    } else if (isGenworthMode && client) {
      navigate(`/clients/${client.clientId}/onboarding/funding-sources`);
    } else if (skipPolicySteps) {
      goToStep(getStepIndex('plan-comparison', flatStepConfigs));
    } else {
      nextStep();
    }
  }, [isConsumerAppMode, isGenworthMode, client, navigate, skipPolicySteps, goToStep, nextStep, flatStepConfigs]);

  const nextButtonLabel = nextButtonLabelFor({
    hasGap,
    isConsumerAppMode,
    isGenworthMode,
    isCcrcTypeA,
    skipPolicySteps,
  });

  return (
    <FundingAssessmentProjection
      assessment={assessment}
      continueActions={[{ label: nextButtonLabel, onSelect: handleNext }]}
      showInsurancePrompt={!skipPolicySteps}
      onBack={previousStep}
      frame={parts => (
        <StepLayout
          title={parts.title}
          subtitle={parts.subtitle}
          icon={parts.icon}
          nextButtonLabel={parts.primaryAction?.label}
          isNextDisabled={parts.primaryAction?.disabled}
          onNext={parts.primaryAction?.onSelect}
          onBack={parts.backAction?.onSelect}
        >
          {parts.content}
        </StepLayout>
      )}
    />
  );
}
